import { Component, Inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthUser } from '@app/account/account-api.model';
import { ChartsService } from '@app/charts/charts.service';
import { FlexRowSummary } from '@app/flex-framework/flex-api.model';
import { FlexService } from '@app/flex-framework/services/flex-framework.service';
import { ComboboxOption } from '@app/shared/components/combobox/combobox.model';
import { AuthService } from '@app/shared/services/auth.service';
import {
  DateRange,
  DateRangeService,
} from '@app/shared/services/date-range.service';
import { LDFlagsService } from '@app/shared/services/ld-flags.service';
import { Group } from '@app/shared/services/reporting/reporting-service';
import { ReportingService } from '@app/shared/services/reporting/reporting.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TargetService } from '@app/target/services/target.service';
import { Target } from '@app/target/target-api.model';
import { TranslateService } from '@ngx-translate/core';
import { orderBy as _orderBy } from 'lodash-es';

export interface GroupOption extends ComboboxOption {
  groupId?: number;
  name?: string;
  enabled?: boolean;
}

@Component({
  selector: 'dgx-flex-insights-view',
  templateUrl: './flex-insights-view.component.html',
})
export class FlexInsightsViewComponent implements OnInit {
  public authUser: AuthUser;
  public dateRange: DateRange;
  public disableRatingsInsights: boolean;
  public groups: GroupOption[] = [];
  public orgId: number;
  public resolver: Promise<any>;
  public sections: FlexRowSummary[];
  public selectedGroup: GroupOption;
  public target: Target;
  public showCombinedPlanInsights =
    this.lDFlagsService.showCombinedPlanInsights;
  public tagFlexSectionDetails = this.flexService.displayInfo.Tag;
  public pathwayFlexSectionDetails = this.flexService.displayInfo.Pathway;
  public inputFlexSectionDetails = this.flexService.displayInfo.Input;

  public i18n = this.translate.instant([
    'OrgReportingCtrl_AllGroups',
    'OrgReportingCtrl_FilterByGroup',
    'OrgReportingCtrl_NoMatchingGroups',
  ]);

  constructor(
    private authService: AuthService,
    private dateRangeService: DateRangeService,
    private flexService: FlexService,
    private lDFlagsService: LDFlagsService,
    private targetService: TargetService,
    private trackerService: TrackerService,
    private translate: TranslateService,
    private reportingService: ReportingService,
    private router: Router
  ) {}

  /** Display trackable links table if it is not disabled, and the target privacy is not private. */
  public get displayTrackableLinks() {
    return !this.target?.isTrackableLinksDisabled && this.target.privacyId > 0;
  }

  public ngOnInit(): void {
    this.trackerService.setLocation('Plan Insights');

    this.authUser = this.authService.authUser;

    const { target, sections } = this.targetService.targetResponse;
    this.target = target;
    this.sections = sections;

    if (
      !this.target?.userCanEditTarget &&
      !this.authUser.defaultOrgInfo.permissions.viewReports
    ) {
      this.router.navigate(['plan', this.target.targetId]);
    }
    this.orgId = this.authUser?.defaultOrgId;
    this.disableRatingsInsights = this.authUser?.disableRatingsInsights;

    this.dateRange = this.dateRangeService.getPastDaysExcludeTodayUTC(30);

    this.initGroups();

    this.trackerService.trackEventData({
      action: 'Plan Insights Viewed',
      properties: {
        planId: this.target.targetId,
        title: this.target.title,
      },
    });
  }

  public ngAfterViewInit(): void {
    this.loadChartsModule();
  }

  public initGroups() {
    this.reportingService.getGroups(this.orgId).subscribe((data: Group[]) => {
      const groups = _orderBy(data, ['name'], ['asc']);
      const emptyOption: GroupOption = {
        groupId: undefined,
        name: this.i18n.OrgReportingCtrl_AllGroups,
        enabled: true,
      };
      this.groups = [emptyOption, ...groups];
    });
  }

  public onChangeGroup(item: GroupOption) {
    this.selectedGroup = item;
  }

  /**
   * Lazy load Charts module
   */
  private loadChartsModule(): void {
    this.resolver = ChartsService.loadModule().then((m) => {
      return m.ComponentResolver;
    });
  }
}
